import type { Article, Photo } from "../types";

const DATABASE_NAME = "travel-journal-maker";
const DATABASE_VERSION = 1;
const DRAFT_STORE_NAME = "drafts";
const CURRENT_DRAFT_KEY = "current";

type StoredPhoto = Omit<Photo, "preview" | "isProcessing">;

interface StoredArticle extends Omit<Article, "sections" | "isLoading" | "progress" | "error"> {
  sections: StoredPhoto[];
}

export async function loadArticleDraft(): Promise<Article | null> {
  const database = await openDatabase();
  if (!database) return null;

  try {
    const transaction = database.transaction(DRAFT_STORE_NAME, "readonly");
    const stored = await requestResult<StoredArticle | undefined>(
      transaction.objectStore(DRAFT_STORE_NAME).get(CURRENT_DRAFT_KEY),
    );
    if (!stored || !Array.isArray(stored.sections)) return null;

    return {
      ...stored,
      sections: stored.sections.map((photo) => ({
        ...photo,
        preview: URL.createObjectURL(photo.file),
        isProcessing: false,
      })),
      isLoading: false,
      error: undefined,
      progress: undefined,
    };
  } finally {
    database.close();
  }
}

export async function saveArticleDraft(article: Article): Promise<void> {
  if (article.sections.length === 0) {
    await clearArticleDraft();
    return;
  }

  const database = await openDatabase();
  if (!database) return;

  try {
    const stored: StoredArticle = {
      articleTitle: article.articleTitle,
      dateRange: article.dateRange,
      versionHistory: article.versionHistory,
      sections: article.sections.map(
        ({ preview: _preview, isProcessing: _isProcessing, ...photo }) => photo,
      ),
    };
    const transaction = database.transaction(DRAFT_STORE_NAME, "readwrite");
    transaction.objectStore(DRAFT_STORE_NAME).put(stored, CURRENT_DRAFT_KEY);
    await transactionComplete(transaction);
  } finally {
    database.close();
  }
}

export async function clearArticleDraft(): Promise<void> {
  const database = await openDatabase();
  if (!database) return;

  try {
    const transaction = database.transaction(DRAFT_STORE_NAME, "readwrite");
    transaction.objectStore(DRAFT_STORE_NAME).delete(CURRENT_DRAFT_KEY);
    await transactionComplete(transaction);
  } finally {
    database.close();
  }
}

function openDatabase(): Promise<IDBDatabase | null> {
  if (!("indexedDB" in window)) return Promise.resolve(null);

  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(DRAFT_STORE_NAME)) {
        request.result.createObjectStore(DRAFT_STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Could not open draft storage"));
  });
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("Draft storage request failed"));
  });
}

function transactionComplete(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(transaction.error ?? new Error("Draft storage transaction failed"));
    transaction.onabort = () =>
      reject(transaction.error ?? new Error("Draft storage transaction was canceled"));
  });
}
